import React from 'react';
import { ArrowLeft, Users, Target, Heart, TrendingUp, MapPin, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

export default function Sobre() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[var(--background)] pb-20">
      <SEOHead
        title="Sobre o Projeto"
        description="Conheça a história e a missão do Empreende Comunidade. Uma plataforma que conecta negócios locais a suas comunidades."
        canonical="/sobre"
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-[#56007F] to-[#7B2FA8] text-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar
          </button>
          <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
            Sobre o Projeto
          </h1>
          <p className="text-white/90 text-lg">
            Conheça a história e a missão do Empreende Comunidade
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Mission Section */}
        <section className="mb-12">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[var(--primary)]/10 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Nossa Missão</h2>
            </div>
            <p className="text-gray-600 leading-relaxed text-lg">
              O <strong>Empreende Comunidade</strong> nasceu com um propósito claro: <span className="text-[var(--primary)] font-medium">fortalecer a economia local</span> conectando consumidores aos negócios do seu bairro, cidade e região.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              Acreditamos que quando o dinheiro circula localmente, toda a comunidade prospera. Cada compra em um negócio local gera empregos, sustenta famílias e fortalece o desenvolvimento regional.
            </p>
          </div>
        </section>

        {/* Values Grid */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope' }}>Nossos Valores</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Comunidade em Primeiro Lugar</h3>
              <p className="text-gray-600 text-sm">
                Todas as nossas decisões são guiadas pelo impacto positivo que podemos gerar na comunidade local.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Desenvolvimento Sustentável</h3>
              <p className="text-gray-600 text-sm">
                Promovemos um crescimento que beneficia a todos, respeitando o meio ambiente e a cultura local.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Heart className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Inclusão e Diversidade</h3>
              <p className="text-gray-600 text-sm">
                Damos visibilidade a todos os tipos de negócios, desde grandes estabelecimentos até pequenos empreendedores.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Lightbulb className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Inovação Acessível</h3>
              <p className="text-gray-600 text-sm">
                Tecnologia de ponta, simples de usar e gratuita para todos os empreendedores da região.
              </p>
            </div>
          </div>
        </section>

        {/* How it Works */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope' }}>Como Funciona</h2>
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[var(--primary)] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <h4 className="font-bold mb-1">Empreendedor cadastra seu negócio</h4>
                  <p className="text-gray-600 text-sm">Processo gratuito e simples, com todas as informações importantes do estabelecimento.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[var(--primary)] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <h4 className="font-bold mb-1">Consumidores encontram negócios</h4>
                  <p className="text-gray-600 text-sm">Busca por categoria, localização, necessidade ou palavras-chave.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[var(--primary)] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <h4 className="font-bold mb-1">Contato direto pelo WhatsApp</h4>
                  <p className="text-gray-600 text-sm">Comunicação rápida e objetiva, sem intermediários.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  ✓
                </div>
                <div>
                  <h4 className="font-bold mb-1">Economia local fortalecida!</h4>
                  <p className="text-gray-600 text-sm">Mais negócios, mais empregos, mais desenvolvimento para a comunidade.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="text-center">
          <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-8">
            <h3 className="text-xl font-bold mb-2">Quer fazer parte dessa história?</h3>
            <p className="text-gray-600 mb-6">
              Cadastre seu negócio gratuitamente ou entre em contato para parcerias.
            </p>
            <button
              onClick={() => navigate('/cadastrar')}
              className="bg-[var(--primary)] text-white hover:bg-[#450066] rounded-full px-8 py-3 font-semibold transition-all shadow-sm hover:shadow-md"
            >
              Cadastrar meu negócio
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
